import * as React from 'react';

import { cn } from '@/lib/utils';

interface TableProps extends React.ComponentProps<'table'> {
  fixedLayout?: boolean;
  minWidth?: number;
  containerClassName?: string;
}

function Table({ className, fixedLayout, minWidth, containerClassName, ...props }: TableProps) {
  return (
    <div data-slot="table-container" className={cn('relative w-full', containerClassName)}>
      <div className={cn('w-full overflow-auto overscroll-none', className)}>
        <table
          data-slot="table"
          className="w-full caption-bottom border-separate border-spacing-0 text-sm"
          style={{ ...(fixedLayout && { tableLayout: 'fixed' }), ...(minWidth && { minWidth }) }}
          {...props}
        />
      </div>
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr:first-child]:border-0', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'h-14 border-b-[0.5px] border-b-stroke/75 transition-colors hover:bg-highlight-background has-data-[state=open]:bg-bg-muted/50',
        className
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'sticky top-0 z-20 h-12 border-y border-stroke bg-surface-card px-2 text-left align-middle font-medium whitespace-nowrap text-foreground-muted first:rounded-l-md first:border-l last:rounded-r-md last:border-r *:[[role=checkbox]]:translate-y-0.5',
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap *:[[role=checkbox]]:translate-y-0.5',
        className
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-foreground-muted', className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
